import { Clock, LoaderCircle, Search, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { PresenceGate } from "@/lib/beui/PresenceGate";
import { cn } from "@/lib/utils";
import { MORPH_ENTER, MORPH_EXIT, SEED, CONTENT_IN, CONTENT_OUT, GLASS, CHIP_HIDDEN, CHIP_SHOWN, CHIP_ENTER, CHIP_EXIT, SURFACE_RADIUS, contains, Highlight } from "./model";
import type { useMorphingSearchChips } from "./use-morphing-search-chips";
export function MorphingSearchChipsView(state: ReturnType<typeof useMorphingSearchChips>) {
const { inputId, addLabel, loading, emptyState, disabled, name, placeholder, label, actions, className, reduce, chips, query, setQuery, recents, setOpen, open, announcement, setMarkedChip, rtl, rootRef, inputRef, chipsRef, id, listId, hintId, optionId, announce, marked, panelSize, measurePanel, setRecents, trimmed, rows, activeIndex, moveTo, showList, panelOpen, panelFloor, measureRows, activeOptionId, close, addChip, removeChip, removeRecent, onKeyDown, focusField, layout, below, seed }=state;
const panel = (
    <>
      <div className="flex h-9 items-center justify-between px-3">
        <span className="text-xs font-medium text-mut">
          {trimmed ? "Suggestions" : "Suggested tags"}
        </span>
        {!trimmed && recents.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setRecents([]);
              announce("Cleared recent searches");
              inputRef.current?.focus();
            }}
            className="rounded-md px-1.5 py-0.5 text-xs text-mut outline-none transition-colors hover:text-navy focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            Clear
          </button>
        ) : null}
      </div>
      {showList ? (
        <div
          id={listId}
          role="listbox"
          aria-label={trimmed ? "Suggestions" : "Tag suggestions"}
          aria-busy={loading || undefined}
          className="max-h-[min(20rem,45vh)] overflow-y-auto overscroll-contain [scrollbar-width:thin]"
        >
          {rows.map((row, index) => {
            const selected = index === activeIndex;
            return (
              // Options are driven from the combobox field (arrows, Enter) and
              // never take focus themselves, as aria-activedescendant expects.
              <motion.div
                key={row.id}
                id={optionId(index)}
                role="option"
                aria-selected={selected}
                onPointerDown={(event) => event.preventDefault()}
                onPointerMove={() => moveTo(row.id)}
                onClick={() => addChip(row.label)}
                className="relative flex h-11 cursor-pointer items-center gap-3 rounded-2xl px-3 text-sm text-navy"
              >
                {selected ? (
                  <motion.span
                    layoutId={`${id}-active`}
                    transition={layout}
                    className="absolute inset-0 rounded-2xl bg-navy/[0.06]"
                  />
                ) : null}
                {row.kind === "recent" ? (
                  <Clock aria-hidden="true" className="relative size-4 shrink-0 text-mut" />
                ) : (
                  <Search aria-hidden="true" className="relative size-4 shrink-0 text-mut" />
                )}
                <span className="relative min-w-0 flex-1 truncate">
                  {row.kind === "query" ? `${addLabel} “${row.label}”` : <Highlight text={row.label} query={trimmed} />}
                </span>
                {row.kind === "recent" ? (
                  // Pointer shortcut only: the option cannot hold a second control,
                  // and Delete removes the highlighted recent from the keyboard.
                  <span
                    aria-hidden="true"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeRecent(row.label);
                    }}
                    className="relative grid size-7 shrink-0 place-items-center rounded-full text-mut transition-colors hover:bg-navy/10 hover:text-navy"
                  >
                    <X className="size-3.5" />
                  </span>
                ) : null}
              </motion.div>
            );
          })}
        </div>
      ) : !trimmed && emptyState != null ? (
        <div className="px-3 pb-2 pt-1 text-sm text-mut">{emptyState}</div>
      ) : null}
      {loading ? (
        <div className="flex h-11 items-center gap-3 px-3 text-sm text-mut">
          <LoaderCircle aria-hidden="true" className="size-4 motion-safe:animate-spin" />
          Searching…
        </div>
      ) : null}
    </>
  );

  return (
    // The root keeps the bar's footprint; the panel floats over the page beside it.
    // biome-ignore lint/a11y/noStaticElementInteractions: Observes focus leaving the whole widget; the field and buttons own interaction.
    <div
      ref={rootRef}
      className={cn(
        "relative flex h-14 w-full max-w-xl justify-center",
        disabled && "opacity-60",
        className,
      )}
      onBlur={(event) => {
        if (rootRef.current?.contains(event.relatedTarget as Node | null)) return;
        setMarkedChip(null);
        close();
      }}
    >
      <AnimatePresence>
        {panelOpen ? (
          <PresenceGate key="panel">
            {({ gate }) => (
              <div
                {...gate}
                className={cn(
                  "absolute inset-x-0 z-10",
                  below ? "top-full mt-2" : "bottom-full mb-2",
                )}
              >
                {/* The card's glass starts as a small pill on the bar's edge and
                    springs out to the size of what it holds, then back on close.
                    Width and height animate rather than scale so the corners stay
                    true; the layer is absolute and decorative, so nothing reflows. */}
                <motion.div
                  aria-hidden="true"
                  initial={reduce ? { opacity: 0 } : { ...seed, opacity: 0 }}
                  animate={{
                    width: panelSize.width || SEED.width,
                    height: panelSize.height || SEED.height,
                    y: 0,
                    opacity: 1,
                  }}
                  exit={
                    reduce
                      ? { opacity: 0, transition: { duration: 0.12 } }
                      : { ...seed, opacity: 0, transition: MORPH_EXIT }
                  }
                  transition={reduce ? { duration: 0 } : MORPH_ENTER}
                  style={{ borderRadius: SURFACE_RADIUS, x: "-50%" }}
                  className={cn("absolute left-1/2", below ? "top-0" : "bottom-0", GLASS)}
                />
                {/* Content waits for the shape to arrive and leaves before it goes. */}
                <motion.div
                  initial={{ opacity: 0, y: reduce ? 0 : below ? -6 : 6 }}
                  animate={{ opacity: 1, y: 0, transition: reduce ? { duration: 0.15 } : CONTENT_IN }}
                  exit={{ opacity: 0, transition: CONTENT_OUT }}
                  ref={measurePanel}
                  // Never shorter than this session's tallest list, so narrowing a
                  // search leaves empty glass instead of pulling the card down.
                  style={{ minHeight: panelFloor || undefined }}
                  className="relative p-2"
                >
                  <div ref={measureRows}>{panel}</div>
                </motion.div>
              </div>
            )}
          </PresenceGate>
        ) : null}
      </AnimatePresence>

      <motion.div
        layout
        transition={layout}
        onPointerDown={focusField}
        style={{ borderRadius: SURFACE_RADIUS }}
        className={cn(
          // Raised over the panel so the growing shape emerges from behind it.
          "relative z-20 flex h-14 min-w-0 items-center gap-2 ps-4",
          GLASS,
          disabled ? "cursor-not-allowed" : "cursor-text",
          // Trailing buttons carry their own inset; bare text needs the full one.
          actions ? "pe-2" : "pe-4",
          "w-full",
        )}
      >
        <Search aria-hidden="true" className="size-5 shrink-0 text-mut" />
            <div
              ref={chipsRef}
              // The padding at each end is where a scrolled chip fades out; with
              // nothing scrolled under it the padding is empty, so the fade is
              // invisible. Relative, so a leaving chip popped out of flow stays
              // inside the scrolling row.
              className={cn(
                "relative flex min-w-0 items-center gap-1.5 overflow-x-auto [scrollbar-width:none]",
                chips.length > 0 &&
                  "-mx-1.5 px-3 [mask-image:linear-gradient(to_right,transparent,#000_12px,#000_calc(100%-12px),transparent)]",
              )}
            >
              <AnimatePresence initial={false} mode="popLayout">
                {chips.map((chip) => (
                  <motion.span
                    key={chip}
                    layout={reduce ? false : "position"}
                    initial={reduce ? { opacity: 0 } : CHIP_HIDDEN}
                    animate={reduce ? { opacity: 1 } : CHIP_SHOWN}
                    exit={
                      reduce
                        ? { opacity: 0, transition: { duration: 0.1 } }
                        : { ...CHIP_HIDDEN, transition: CHIP_EXIT }
                    }
                    transition={reduce ? { duration: 0.12 } : CHIP_ENTER}
                    // Grown from and folded back into the edge it is attached to, so a
                    // chip reads as unrolling out of the one before it.
                    style={{ transformOrigin: rtl ? "right center" : "left center" }}
                    className={cn(
                      "flex h-9 shrink-0 items-center gap-1 rounded-full ps-3 pe-1 text-sm text-navy transition-[background-color,box-shadow] duration-150",
                      marked === chip
                        ? // Inset: the chip fills its scrolling row, which clips anything drawn outside it.
                        "bg-navy/[0.12] ring-2 ring-inset ring-navy/40"
                        : "bg-navy/[0.07]",
                    )}
                  >
                    <span className="max-w-40 truncate" title={chip}>
                      {chip}
                    </span>
                    <button
                      type="button"
                      disabled={disabled}
                      aria-label={`Remove tag ${chip}`}
                      onClick={() => removeChip(chip)}
                      className="grid size-7 shrink-0 place-items-center rounded-full text-mut outline-none transition-colors hover:bg-navy/10 hover:text-navy focus-visible:ring-2 focus-visible:ring-navy-500 disabled:pointer-events-none"
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </button>
                  </motion.span>
                ))}
              </AnimatePresence>
            </div>
            <motion.input
              id={inputId}
              ref={inputRef}
              // Glides aside as chips arrive and back as they leave.
              layout={reduce ? false : "position"}
              transition={layout}
              type="text"
              role="combobox"
              aria-label={label}
              aria-expanded={panelOpen}
              aria-controls={panelOpen && showList ? listId : undefined}
              aria-autocomplete="list"
              aria-activedescendant={activeOptionId}
              aria-describedby={hintId}
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="search"
              disabled={disabled}
              value={query}
              placeholder={placeholder}
              onChange={(event) => {
                setQuery(event.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              // Escape closes the panel but leaves focus here; a click brings it back.
              onClick={() => setOpen(true)}
              onKeyDown={onKeyDown}
              // At rest the field is as wide as its placeholder, so the bar hugs its content.
              style={open ? undefined : { width: `${Math.max(placeholder.length, 6) + 2}ch` }}
              className={cn(
                // On a narrow screen the chips scroll first; the field keeps room to type.
                "h-9 min-w-24 text-ellipsis bg-transparent text-base text-navy outline-none placeholder:text-mut disabled:cursor-not-allowed",
                open && "flex-1",
              )}
            />
            {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
      </motion.div>

      {name
        ? chips.map((chip) => <input key={chip} type="hidden" name={name} value={chip} />)
        : null}
      <span id={hintId} className="sr-only">
        Enter adds a tag. Backspace at the start of the field selects the last tag, and
        again removes it.
      </span>
      <span aria-live="polite" className="sr-only">
        {announcement.text}
        {/* Alternates an invisible character so a repeated message still changes. */}
        {announcement.count % 2 ? " " : ""}
      </span>
    </div>
  );

}
